// frontend/src/components/wizard/StepMapping/QualityRulesModal.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguageStore } from '../../../store/useLanguageStore';
import { apiClient } from '../../../api/client';
import {
  X, ShieldCheck, ShieldBan, Plus, Trash2, AlertTriangle,
  AlertCircle, Fingerprint, ListChecks, ArrowLeftRight,
  MoveHorizontal, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';
import { QUALITY_RULES, UNIVERSAL_REGEX_PRESETS, VIOLATION_MODES } from '../../../utils/qualityRules';

/**
 * Modal para configurar quality rules en un campo destino específico.
 *
 * Props:
 *   isOpen        — boolean
 *   onClose       — () => void
 *   targetField   — string: nombre del campo destino
 *   qualityRules  — array: reglas actuales del campo (puede ser [] o undefined)
 *   onSave        — (rules: array) => void: callback con las reglas actualizadas
 */
export function QualityRulesModal({ isOpen, onClose, targetField, qualityRules, onSave }) {
  const { t } = useLanguageStore();

  // Local state: copia editable de las reglas
  const [rules, setRules] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  const [presetTarget, setPresetTarget] = useState(null); // índice de la regla REGEX activa
  const [countryPresets, setCountryPresets] = useState([]);
  const [loadingPresets, setLoadingPresets] = useState(false);

  // Inicializar desde props
  useEffect(() => {
    if (isOpen) {
      setRules(Array.isArray(qualityRules) ? qualityRules.map(r => ({ ...r })) : []);
      setShowPresets(false);
      setPresetTarget(null);
    }
  }, [isOpen, qualityRules]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // ── Handlers ─────────────────────────────────────────────
  const addRule = (ruleType) => {
    const ruleDef = QUALITY_RULES.find(r => r.id === ruleType);
    if (!ruleDef) return;

    // Evitar duplicados del mismo tipo
    if (rules.some(r => r.ruleType === ruleType)) return;

    const newRule = {
      ruleType,
      onViolation: 'warn',
      params: {},
    };

    // Inicializar params según tipo
    if (ruleType === 'RANGE') newRule.params = { min: null, max: null };
    if (ruleType === 'ALLOWED_VALUES') newRule.params = { values: [] };
    if (ruleType === 'REGEX') newRule.params = { pattern: '' };
    if (ruleType === 'MIN_LENGTH' || ruleType === 'MAX_LENGTH') newRule.params = { length: '' };

    setRules(prev => [...prev, newRule]);
  };

  const removeRule = (idx) => {
    setRules(prev => prev.filter((_, i) => i !== idx));
  };

  const updateRule = (idx, field, value) => {
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };

  const updateParam = (idx, paramKey, value) => {
    setRules(prev => prev.map((r, i) =>
      i === idx ? { ...r, params: { ...r.params, [paramKey]: value } } : r
    ));
  };

  const handleSave = () => {
    // Filtrar reglas vacías o inválidas
    const cleaned = rules.filter(r => r.ruleType);
    onSave(cleaned.length > 0 ? cleaned : []);
    onClose();
  };

  // ── Presets ───────────────────────────────────────────────
  const openPresets = async (ruleIdx) => {
    setPresetTarget(ruleIdx);
    setShowPresets(true);
    // Cargar presets por país si no están cargados
    if (countryPresets.length === 0) {
      setLoadingPresets(true);
      try {
        // const res = await apiClient.get('/catalogs/validation-presets');
        const res = await apiClient.get('/catalogs/validation-presets?mine=1');
        if (res.data?.presets) {
          setCountryPresets(res.data.presets);
        }
      } catch {
        // Silent — presets por país no son críticos
      } finally {
        setLoadingPresets(false);
      }
    }
  };

  const applyPreset = (pattern) => {
    if (presetTarget !== null) {
      updateParam(presetTarget, 'pattern', pattern);
    }
    setShowPresets(false);
    setPresetTarget(null);
  };

  // ── Allowed Values tag input ─────────────────────────────
  const [tagInput, setTagInput] = useState('');

  const handleTagKeyDown = (e, idx) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim();
      if (val && !(rules[idx]?.params?.values || []).includes(val)) {
        const newVals = [...(rules[idx]?.params?.values || []), val];
        updateParam(idx, 'values', newVals);
      }
      setTagInput('');
    }
  };

  const removeTag = (idx, tagIdx) => {
    const newVals = (rules[idx]?.params?.values || []).filter((_, i) => i !== tagIdx);
    updateParam(idx, 'values', newVals);
  };

  // ── Icon map ─────────────────────────────────────────────
  const ICON_MAP = {
    AlertCircle, Fingerprint, ListChecks, ArrowLeftRight, MoveHorizontal,
  };

  const getRuleIcon = (ruleId) => {
    const def = QUALITY_RULES.find(r => r.id === ruleId);
    const IconComp = def?.icon ? ICON_MAP[def.icon] : ShieldCheck;
    return IconComp || ShieldCheck;
  };

  // ── Available rules (not yet added) ──────────────────────
  const usedTypes = new Set(rules.map(r => r.ruleType));
  const availableRules = QUALITY_RULES.filter(r => !usedTypes.has(r.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">

        {/* ── Header ─────────────────────────────────────── */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-50 rounded-xl">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                {t('data_rules.title')}
              </h3>
              <p className="text-sm text-slate-500">
                {targetField}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl transition">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* ── Body ──────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Reglas configuradas */}
          {rules.length === 0 && (
            <div className="text-center py-8 text-slate-400">
              <ShieldCheck className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>{t('data_rules.no_rules')}</p>
            </div>
          )}

          {rules.map((rule, idx) => {
            const RuleIcon = getRuleIcon(rule.ruleType);
            return (
              <div key={idx} className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">

                {/* Rule header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <RuleIcon className="w-4 h-4 text-emerald-600" />
                    <span className="font-semibold text-slate-700 text-sm">
                      {t(`data_rules.rule_${rule.ruleType}`)}
                    </span>
                  </div>
                  <button
                    onClick={() => removeRule(idx)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition"
                    title={t('common.delete')}
                  >
                    <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                  </button>
                </div>

                {/* Description */}
                <p className="text-xs text-slate-500">
                  {t(`data_rules.desc_${rule.ruleType}`)}
                </p>

                {/* ── Params por tipo ───────────────────── */}

                {/* REGEX */}
                {rule.ruleType === 'REGEX' && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600">{t('data_rules.param_pattern')}</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={rule.params?.pattern || ''}
                        maxLength={500}
                        onChange={(e) => updateParam(idx, 'pattern', e.target.value)}
                        placeholder={t('data_rules.param_pattern_placeholder')}
                        className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-300 focus:border-emerald-400 outline-none font-mono"
                      />
                      <button
                        onClick={() => openPresets(idx)}
                        className="px-3 py-2 text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition flex items-center gap-1"
                        title={t('data_rules.preset_title')}
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Presets
                      </button>
                    </div>
                  </div>
                )}

                {/* RANGE */}
                {rule.ruleType === 'RANGE' && (
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-600">{t('data_rules.param_min')}</label>
                      <input
                        type="number"
                        value={rule.params?.min ?? ''}
                        onChange={(e) => updateParam(idx, 'min', e.target.value === '' ? null : Number(e.target.value))}
                        className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-300 outline-none"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-600">{t('data_rules.param_max')}</label>
                      <input
                        type="number"
                        value={rule.params?.max ?? ''}
                        onChange={(e) => updateParam(idx, 'max', e.target.value === '' ? null : Number(e.target.value))}
                        className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-300 outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* MIN_LENGTH / MAX_LENGTH */}
                {(rule.ruleType === 'MIN_LENGTH' || rule.ruleType === 'MAX_LENGTH') && (
                  <div>
                    <label className="text-xs font-medium text-slate-600">{t('data_rules.param_length')}</label>
                    <input
                      type="number"
                      min="1"
                      max={10000}
                      value={rule.params?.length || ''}
                      onChange={(e) => updateParam(idx, 'length', e.target.value === '' ? '' : parseInt(e.target.value))}
                      className="w-32 mt-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-300 outline-none"
                    />
                  </div>
                )}

                {/* ALLOWED_VALUES */}
                {rule.ruleType === 'ALLOWED_VALUES' && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600">{t('data_rules.param_values')}</label>
                    <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 border border-slate-300 rounded-lg bg-white">
                      {(rule.params?.values || []).map((val, tagIdx) => (
                        <span
                          key={tagIdx}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-md text-xs"
                        >
                          {val}
                          <button onClick={() => removeTag(idx, tagIdx)} className="hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) => handleTagKeyDown(e, idx)}
                        maxLength={100}
                        onBlur={() => {
                          if (tagInput.trim()) {
                            const val = tagInput.trim();
                            if (!(rules[idx]?.params?.values || []).includes(val)) {
                              updateParam(idx, 'values', [...(rules[idx]?.params?.values || []), val]);
                            }
                            setTagInput('');
                          }
                        }}
                        placeholder={t('data_rules.param_values_placeholder')}
                        className="flex-1 min-w-[140px] px-1 py-0.5 text-sm outline-none border-none"
                      />
                    </div>
                  </div>
                )}

                {/* NOT_NULL y UNIQUE no tienen params */}

                {/* ── On Violation selector ─────────────── */}
                <div className="pt-2 border-t border-slate-200">
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">{t('data_rules.on_violation')}</label>
                  <div className="flex gap-2">
                    {VIOLATION_MODES.map(mode => (
                      <button
                        key={mode.value}
                        onClick={() => updateRule(idx, 'onViolation', mode.value)}
                        className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5 transition ${rule.onViolation === mode.value
                          ? mode.value === 'block'
                            ? 'bg-red-50 text-red-700 ring-2 ring-red-300'
                            : 'bg-amber-50 text-amber-700 ring-2 ring-amber-300'
                          : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-50'
                          }`}
                      >
                        {mode.value === 'warn'
                          ? <AlertTriangle className="w-3.5 h-3.5" />
                          : <ShieldBan className="w-3.5 h-3.5" />}
                        {t(`data_rules.mode_${mode.value}`)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Add rule dropdown ────────────────────────── */}
          {availableRules.length > 0 && (
            <div className="pt-2">
              <p className="text-xs font-medium text-slate-500 mb-2">{t('data_rules.add_rule')}</p>
              <div className="grid grid-cols-2 gap-2">
                {availableRules.map(ruleDef => {
                  const RuleIcon = ICON_MAP[ruleDef.icon] || ShieldCheck;
                  return (
                    <button
                      key={ruleDef.id}
                      onClick={() => addRule(ruleDef.id)}
                      className="flex items-center gap-2 px-3 py-2.5 text-left text-sm text-slate-600 bg-white border border-dashed border-slate-300 rounded-xl hover:border-emerald-400 hover:bg-emerald-50/30 transition"
                    >
                      <Plus className="w-4 h-4 text-emerald-500 shrink-0" />
                      <span>{t(`data_rules.rule_${ruleDef.id}`)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Presets Panel ────────────────────────────── */}
          {showPresets && (
            <div className="border border-emerald-200 rounded-xl p-4 bg-emerald-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-emerald-800">{t('data_rules.preset_title')}</h4>
                <button onClick={() => setShowPresets(false)} className="p-1 hover:bg-emerald-100 rounded-lg">
                  <X className="w-4 h-4 text-emerald-600" />
                </button>
              </div>

              {/* Universal presets */}
              <div>
                <p className="text-xs font-medium text-emerald-700 mb-1.5">{t('data_rules.preset_universal')}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {UNIVERSAL_REGEX_PRESETS.map(p => (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p.pattern)}
                      className="text-left px-3 py-2 text-xs bg-white rounded-lg border border-emerald-200 hover:border-emerald-400 transition"
                    >
                      <span className="font-medium text-slate-700">{p.label}</span>
                      <span className="block text-slate-400 font-mono text-[10px] mt-0.5 truncate">{p.pattern}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Country presets */}
              {countryPresets.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-emerald-700 mb-1.5">{t('data_rules.preset_by_country')}</p>
                  <div className="space-y-3">
                    {Object.entries(
                      countryPresets.reduce((acc, cp) => {
                        if (!acc[cp.country]) acc[cp.country] = [];
                        // Deduplicar por pattern dentro del mismo país
                        if (!acc[cp.country].some(p => p.pattern === cp.pattern)) {
                          acc[cp.country].push(cp);
                        }
                        return acc;
                      }, {})
                    ).map(([country, presets]) => (
                      <div key={country}>
                        <p className="text-[10px] font-semibold text-emerald-800 mb-1">{country} ({presets[0]?.countryCode2})</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {presets.map((cp, i) => (
                            <button
                              key={`${cp.countryCode2}-${cp.key}`}
                              onClick={() => applyPreset(cp.pattern)}
                              className="text-left px-3 py-2 text-xs bg-white rounded-lg border border-emerald-200 hover:border-emerald-400 transition"
                            >
                              <span className="font-medium text-slate-700">{cp.label}</span>
                              <span className="block text-slate-400 font-mono text-[10px] mt-0.5 truncate">{cp.pattern}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loadingPresets && (
                <p className="text-xs text-emerald-600 animate-pulse">Loading...</p>
              )}
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────── */}
        <div className="flex items-center justify-between p-5 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <span className="text-xs text-slate-500">
            {rules.length > 0
              ? t('data_rules.rules_count').replace('{count}', rules.length)
              : ''}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-xl transition"
            >
              {t('common.buttons.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-sm bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition shadow-sm"
            >
              {t('common.buttons.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
